import { formatCurrency, formatCrypto } from "@/lib/crypto-data";
import { CheckCircle2, Clock, XCircle, CreditCard, Wallet, Network } from "lucide-react";
import { format } from "date-fns";
import { useTranslation } from "react-i18next";

interface Transaction {
  id: string;
  crypto_symbol: string;
  crypto_name: string;
  amount: number;
  price_usd: number;
  total_usd: number;
  card_last_four: string;
  card_type: string;
  status: string;
  created_at: string;
  wallet_address?: string;
  network?: string;
  network_fee?: number;
  transaction_number?: string;
}

interface TransactionRowProps {
  transaction: Transaction;
}

const TransactionRow = ({ transaction }: TransactionRowProps) => {
  const { t } = useTranslation();

  const statusConfig = {
    completed: {
      icon: CheckCircle2,
      text: t('transactions.completed'),
      color: "text-crypto-green",
      bg: "bg-crypto-green/20",
    },
    approved: {
      icon: CheckCircle2,
      text: t('transactions.completed'),
      color: "text-crypto-green",
      bg: "bg-crypto-green/20",
    },
    pending: {
      icon: Clock,
      text: t('transactions.pending'),
      color: "text-crypto-gold",
      bg: "bg-crypto-gold/20",
    },
    failed: {
      icon: XCircle,
      text: t('transactions.failed'),
      color: "text-crypto-red",
      bg: "bg-crypto-red/20",
    },
    rejected: {
      icon: XCircle,
      text: t('transactions.failed'),
      color: "text-crypto-red",
      bg: "bg-crypto-red/20",
    },
  };

  const status = statusConfig[transaction.status as keyof typeof statusConfig] || statusConfig.pending;
  const StatusIcon = status.icon;

  return (
    <div className="p-4 rounded-xl glass hover:bg-secondary/30 transition-colors animate-fade-in">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center">
            <span className="text-xl font-bold text-primary">{transaction.crypto_symbol[0]}</span>
          </div>
          <div>
            <h4 className="font-semibold text-foreground">
              {t('transactions.buy')} {formatCrypto(transaction.amount)} {transaction.crypto_symbol}
            </h4>
            <div className="flex items-center gap-2">
              {transaction.transaction_number && (
                <span className="text-xs font-mono text-primary bg-primary/10 px-1.5 py-0.5 rounded">
                  {transaction.transaction_number}
                </span>
              )}
              <p className="text-sm text-muted-foreground">
                {format(new Date(transaction.created_at), "dd MMM yyyy - HH:mm")}
              </p>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-6">
          <div className="text-right">
            <p className="font-semibold text-foreground">{formatCurrency(transaction.total_usd)}</p>
            <p className="text-sm text-muted-foreground">@ {formatCurrency(transaction.price_usd)}</p>
          </div>
          <div className={`flex items-center gap-1 px-3 py-1 rounded-full ${status.bg}`}>
            <StatusIcon className={`w-4 h-4 ${status.color}`} />
            <span className={`text-sm font-medium ${status.color}`}>{status.text}</span>
          </div>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-4 pt-3 border-t border-border/50 text-sm">
        <div className="flex items-center gap-2 text-muted-foreground">
          <CreditCard className="w-4 h-4" />
          <span>{transaction.card_type} •••• {transaction.card_last_four}</span>
        </div>
        {transaction.network && (
          <div className="flex items-center gap-2 text-muted-foreground">
            <Network className="w-4 h-4" />
            <span>{transaction.network}</span>
            {transaction.network_fee !== undefined && transaction.network_fee > 0 && (
              <span className="text-primary">({t('transactions.fee')}: {formatCurrency(transaction.network_fee)})</span>
            )}
          </div>
        )}
        {transaction.wallet_address && (
          <div className="flex items-center gap-2 text-muted-foreground flex-1 min-w-0">
            <Wallet className="w-4 h-4 flex-shrink-0" />
            <span className="truncate font-mono text-xs" dir="ltr">{transaction.wallet_address}</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default TransactionRow;
